import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import CTLNotification from "application/components/CTLNotification";
import DataTable, { createTheme } from "react-data-table-component";
import colors from "assets/theme-dark/base/colors";
import DeleteConfirmation from "application/components/DeleteConfirmation";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { header, Attributes, operationURLS } from "./model";
import { application } from "globals/endpoints";
import BackupDialog from "./backupDialog";
import loggerFactory from "globals/logger/logger-factory";
import BackupUpload from "./backupUpload";
import { useNavigate } from "react-router-dom";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

export default function SystemBackupRestore({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [controller] = useMaterialUIController();
  const { activePage, darkMode } = controller;
  const darkModeContext = useMaterialUIController();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ViewData, setViewData] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState("");
  const navigate = useNavigate();
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    if (Attributes && onLoad) {
      const isViewOnly = AuthenticationService.isViewOnlyUser();
      onLoad({
        ...Attributes,
        enableBackupButton: Attributes.enableBackupButton && !isViewOnly,
        enableUploadButton: Attributes.enableUploadButton && !isViewOnly,
      });
    }

    if (registerCallBacks) {
      registerCallBacks({
        onBackup: handleOpenBackupDialog,
        onRefresh: onRefresh,
        onUpload: handleOpenUploadDialog,
      });
    }
  }, [activePage]);

  useEffect(() => {
    GetData();
  }, [activePage]);

  function GetData() {
    application
      .get(operationURLS.list)
      .then((res) => {
        setViewData(res.data.data);
      })
      .catch((error) => {
        console.log("error while the backup list api ", error);
      });
  }

  const onRefresh = () => {
    GetData();
    setNotify({
      isOpen: true,
      message: "",
      type: "info",
      pagename: pageName,
      status: "Backup List Refreshed",
    });
  };

  const deleteRow = (row) => {
    setSelectedRow(row);
    console.log("Selected row for delete: ", row);
    setConfirmDelete(true);
  };

  const onCancel = () => {
    setSelectedRow([]);
    setConfirmDelete(false);
  };

  const onAgree = (row) => {
    application
      .delete(`${operationURLS.delete}`, {
        params: { timestamp: row?.timestamp },
      })
      .then((response) => {
        setNotify({
          isOpen: true,
          status: response?.data?.message,
          type: "success",
          message: response?.data?.data?.filename,
          pagename: pageName,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message:
            (error?.response?.data?.message || "") +
            " " +
            (error?.response?.data?.messageDetail || ""),
          type: "error",
          pagename: pageName,
          status: error?.response?.status,
        });
      })
      .finally(() => {
        setConfirmDelete(false);
        GetData();
      });
  };
  const onRestore = async (row) => {
    let skipFinally = false;

    setLoading(true);
    setLoadingMessage("Restoring backup... Please wait.");

    if (!row?.path) {
      setNotify({
        isOpen: true,
        message: "Invalid file. Cannot restore.",
        type: "error",
        pagename: pageName,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await application.post(
        operationURLS.restoreConfiguration,
        null,
        {
          params: { archivePath: row.filename },
        }
      );
      const msg = response?.data?.message || "";
      if (msg.includes("SYSTEM REBOOTING IN 10 SECONDS")) {
        skipFinally = true;
        let seconds = 5;
        setLoading(true);

        const timer = setInterval(() => {
          setLoadingMessage(
            `System is rebooting in ${seconds} sec... Please wait.`
          );
          seconds--;

          if (seconds < 0) {
            clearInterval(timer);
            setLoadingMessage("Please login again to continue...");
            setTimeout(() => {
              setLoading(false);
              setLoadingMessage("");
            }, 2000);
          }
        }, 1000);

        return;
      }
      setNotify({
        isOpen: true,
        status: response?.data?.message,
        type: "success",
        message: "Restore completed successfully",
        pagename: pageName,
      });

      GetData();
    } catch (error) {
      setNotify({
        isOpen: true,
        status: error?.response?.data?.message || "Restore failed",
        type: "error",
        pagename: pageName,
      });
    } finally {
      if (!skipFinally) {
        setLoading(false);
        setLoadingMessage("");
      }
    }
  };

  const onDownload = async (row) => {
    setLoading(true);
    setLoadingMessage("Preparing download backup...");

    try {
      const response = await application.get(operationURLS.download, {
        params: { timestamp: row.timestamp },
        responseType: "blob",
      });

      if (response.data.type === "application/json") {
        const text = await response.data.text();
        const errorJson = JSON.parse(text);
        throw new Error(errorJson?.messageDetail || errorJson?.message);
      }

      const disposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"] ||
        "";
      const match = disposition.match(
        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/
      );

      const filename = decodeURIComponent(
        match?.[1] || match?.[2] || row.filename
      );

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      setNotify({
        isOpen: true,
        type: "success",
        status: `Downloaded: ${filename}`,
        pagename: pageName,
      });

      GetData();
    } catch (error) {
      setNotify({
        isOpen: true,
        type: "error",
        status: error.message || "Download failed",
        pagename: pageName,
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleOpenBackupDialog = () => {
    setOpen(true);
  };

  const handleOpenUploadDialog = () => {
    setUploadOpen(true);
  };

  const handleSubmitBackup = (selectedOption) => {
    if (!selectedOption) return;
    setLoading(true);
    setLoadingMessage("Creating backup...");
    loggerFactory.debug("Selected Option: ", selectedOption);
    const backupURL =
      selectedOption === "config"
        ? operationURLS.createBackupConfiguration
        : operationURLS.createBackupDatabase;

    application
      .post(backupURL)
      .then((response) => {
        setNotify({
          isOpen: true,
          status: response.data.message,
          type: "success",
          pagename: pageName,
        });
        GetData();
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status: error?.response?.data?.message || "Something went wrong!",
          type: "error",
          pagename: pageName,
        });
      })
      .finally(() => {
        setLoading(false);
        setLoadingMessage("");
        setOpen(false);
        GetData();
      });
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setLoadingMessage("Uploading backup file...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await application.post(
        "/node-control/backup/system/configuration/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setNotify({
        isOpen: true,
        status: response.data.message,
        type: "success",
        pagename: pageName,
      });
      GetData();
    } catch (error) {
      setNotify({
        isOpen: true,
        status: error?.response?.data?.message || "File upload failed!",
        type: "error",
        pagename: pageName,
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
      setUploadOpen(false);
    }
  };

  createTheme(
    "modifiedDarkTheme",
    {
      striped: {
        default: colors.background.default,
      },
      selected: {
        default: colors.background.default,
      },
      background: {
        default: colors.background.card,
      },
    },
    "dark"
  );

  return (
    <MDBox>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "rgba(5, 5, 5, 0.78)",
            zIndex: 2000,
            flexDirection: "column",
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" sx={{ color: "#fff", marginTop: 2 }}>
            {loadingMessage || "Please wait..."}
          </Typography>
        </Box>
      )}

      <DeleteConfirmation
        entityName={pageName}
        data={selectedRow}
        columns={header(deleteRow, onRestore, onDownload)}
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
      />

      <DataTable
        columns={header(deleteRow, onRestore, onDownload)}
        data={ViewData}
        canSearch
        responsive
        pagination
        striped
        paginationPerPage={10}
        paginationRowsPerPageOptions={[5, 10, 20]}
        theme={darkModeContext[0].darkMode ? "modifiedDarkTheme" : "default"}
        fixedHeader
        persistTableHead
        fixedHeaderScrollHeight={`${(window.innerHeight / 100) * 66}px`}
      />

      <CTLNotification notify={notify} setNotify={setNotify} />
      <BackupDialog
        open={open}
        setOpen={setOpen}
        handleSubmitBackup={handleSubmitBackup}
        loading={loading}
      />

      <BackupUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploadFile={onUploadFile}
        loading={loading}
      />
    </MDBox>
  );
}
